import {
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { useMemo } from 'react';

import { tPortalLib } from '../../locales';
import { PortalTheme } from '../../theme';

export interface VoucherCardProps {
  title: string;
  formatRelativeDatetime: (date: Date) => string;
  validTo?: string;
  availableSites?: string;
  description?: string;
  headerImageUrl?: string;
  headerImageAltText?: string;
  claims?: number;
  maxClaims?: number;
  lastClaimDate?: string;
  bannerText?: string;
  bannerColor?: string;
  bannerTextColor?: string;
  code?: string;
  onClick?: () => void;
}

export const VoucherCard = (props: VoucherCardProps) => {
  const theme = useTheme<PortalTheme>();

  const validUntilDate = useMemo(() => {
    if (!props.validTo) {
      return '';
    }

    const date = new Date(props.validTo);

    return props.formatRelativeDatetime(date);
  }, [props.validTo, props.formatRelativeDatetime]);

  const redeemed = useMemo(() => {
    return (
      props.claims !== undefined &&
      props.maxClaims !== undefined &&
      props.claims >= props.maxClaims
    );
  }, [props.claims, props.maxClaims]);

  const usageChip = useMemo(() => {
    if (redeemed && props.lastClaimDate) {
      return (
        <Chip
          label={tPortalLib.vouchers.claimed({
            claimDate: props.formatRelativeDatetime(
              new Date(props.lastClaimDate),
            ),
            interpolation: { escapeValue: false },
          })}
          sx={{
            ...theme.typography.subtitle3,
            color: 'error.main',
            backgroundColor: 'error.light',
          }}
          color="error"
          size="small"
        />
      );
    }

    if (props.validTo && new Date(props.validTo) < new Date()) {
      return (
        <Chip
          label={tPortalLib.vouchers.expired({
            expirationDate: props.formatRelativeDatetime(
              new Date(props.validTo),
            ),
            interpolation: { escapeValue: false },
          })}
          sx={{
            ...theme.typography.subtitle3,
            color: 'error.main',
            backgroundColor: 'error.light',
          }}
          color="error"
          size="small"
        />
      );
    }

    if (props.maxClaims === 1) {
      return (
        <Chip
          label={tPortalLib.vouchers.oneTimeUsage()}
          sx={{ ...theme.typography.subtitle3 }}
          size="small"
        />
      );
    }

    if (props.claims !== undefined && props.maxClaims !== undefined) {
      return (
        <Chip
          label={tPortalLib.vouchers.uses({
            claims: props.claims.toString(),
            maxClaims: props.maxClaims.toString(),
          })}
          sx={{ ...theme.typography.subtitle3 }}
          size="small"
        />
      );
    }

    return null;
  }, [
    props.claims,
    props.maxClaims,
    props.lastClaimDate,
    props.validTo,
    theme.typography.subtitle3,
    props.formatRelativeDatetime,
  ]);

  return (
    <Card
      sx={{
        width: '100%',
        borderRadius: `2*${theme.shape.borderRadius}px`,
        border: `1px solid ${theme.palette.divider}`,
        padding: 0,
        margin: 0,
        backgroundColor: `${theme.palette.background.paper}`,
        boxShadow: theme.boxShadow,
        position: 'relative',
      }}
      onClick={() => props.onClick?.()}
    >
      {props.headerImageUrl && (
        <CardMedia
          component="img"
          sx={{ aspectRatio: '3/2' }}
          image={props.headerImageUrl}
          alt={props.headerImageAltText}
          loading="lazy"
        />
      )}
      {props.bannerText && (
        <Typography
          variant="subtitle3"
          color="textSecondary"
          component="a"
          sx={{
            ...theme.typography.subtitle2,
            color: props.bannerTextColor || theme.palette.primary.contrastText,
            backgroundColor: props.bannerColor || theme.palette.primary.main,
            position: 'absolute',
            top: 0,
            right: 0,
            lineHeight: '1.8',
            paddingInline: '1lh',
            transform:
              'translate(calc((1 - cos(45deg))*100%), -100%) rotate(45deg)',
            transformOrigin: '0% 100%',
            minWidth: '100px',
            maxWidth: '200px',
            textAlign: 'center',
          }}
        >
          {props.bannerText}
        </Typography>
      )}

      <CardContent sx={{ ':last-child': { paddingBottom: '16px' } }}>
        <Stack direction="column" spacing={0.5} sx={{ mb: '24px' }}>
          {validUntilDate !== '' && (
            <Typography variant="subtitle4" color="textSecondary">
              {tPortalLib.vouchers.validUntil({
                validUntil: validUntilDate,
                interpolation: { escapeValue: false },
              })}
            </Typography>
          )}
          <Typography variant="h4" color="textPrimary" sx={{ width: '100%' }}>
            {props.title}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {props.description}
          </Typography>
          <Typography variant="subtitle3" color="textSecondary" component="a">
            {props.availableSites
              ? tPortalLib.vouchers.validAt({
                  validAt: props.availableSites,
                })
              : tPortalLib.vouchers.validAt({
                  validAt: tPortalLib.vouchers.allSites(),
                })}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          {usageChip}
          <Typography variant="subtitle3" color="textSecondary">
            {props.code
              ? tPortalLib.vouchers.manualVoucher()
              : tPortalLib.vouchers.automaticVoucher()}
          </Typography>
        </Stack>
        {props.code && !redeemed && (
          <Button
            variant="contained"
            color="primary"
            sx={{ mt: '24px', width: '100%' }}
            onClick={() => props.onClick?.()}
          >
            {tPortalLib.vouchers.openToUse()}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};
